import type { RoleKey } from "./types";

export interface RoleOption {
  key: RoleKey;
  label: string;
  relationshipType: string;
  roleTitle: string | null;
  iconKey?: string;
}

interface RawDimension {
  name?: string;
  question?: string;
  values?: Record<string, string>;
  default_values?: Record<string, string>;
}

/**
 * ED dimensions from the live index. Empty until `entity_index_dimensions` has been
 * fetched (there is no bundled fallback — see configApi's EMPTY_INDEXES); callers
 * degrade to the hardcoded English labels below for that window.
 */
function getDimensions(entityIndex?: Record<string, unknown> | null): Record<string, RawDimension> {
  return (entityIndex?.dimensions as Record<string, RawDimension>) ?? {};
}

function dimValues(code: string, dimensions: Record<string, RawDimension>): Record<string, string> {
  const dim = dimensions[code];
  if (!dim) return {};
  return dim.values ?? dim.default_values ?? {};
}

/**
 * Build role options from the entity dimension index.
 *
 * Source dimensions:
 *  - ED130 Relationship Context (primary)
 *  - ED20 Entity Subtype (freelancer, investor, etc.)
 *  - ED22 Department (employee specialisations)
 *
 * The wizard asks "how do you relate to this entity?" We only show options that
 * make sense for the scraped entity's level (L0 company, L1 product, L2 function,
 * L3 person). The index is the source of truth; the mapping below wires index
 * values to UI labels and backend relationship types.
 */
export function getRoleOptionsForEntityLevel(
  level: "L0" | "L1" | "L2" | "L3" | string,
  entityIndex?: Record<string, unknown> | null,
): RoleOption[] {
  const dimensions = getDimensions(entityIndex);
  const relationshipContext = dimValues("ED130", dimensions);
  const departments = dimValues("ED22", dimensions);
  const subtypes = dimValues("ED20", dimensions);

  // Base relationship options derived from ED130
  const base: RoleOption[] = [
    { key: "founder_ceo", label: "Owner / Founder", relationshipType: "governance_role", roleTitle: "Owner / Founder" },
    { key: "investor",    label: relationshipContext["7"] ?? "Investor",    relationshipType: "investor",      roleTitle: "Investor" },
    { key: "partner",     label: relationshipContext["5"] ?? "Partner",     relationshipType: "partner",       roleTitle: "Partner" },
    { key: "supplier",    label: relationshipContext["3"] ?? "Supplier",    relationshipType: "supplier",      roleTitle: "Supplier" },
    { key: "client",      label: relationshipContext["4"] ?? "Client",      relationshipType: "client",        roleTitle: "Client" },
    { key: "freelancer",  label: subtypes["6"] ?? "Freelancer",             relationshipType: "employee",      roleTitle: "Freelancer" },
  ];

  // Employee roles come from ED22 Department values
  const employeeRoles: RoleOption[] = Object.entries(departments).map(([code, dept]) => ({
    key: `employee_${code}` as RoleKey,
    label: dept,
    relationshipType: "employee",
    roleTitle: dept,
  }));

  // Level-specific tweaks
  switch (level) {
    case "L0": // Company / Organisation
      return [
        { key: "founder_ceo", label: "Founder / CEO", relationshipType: "governance_role", roleTitle: "Founder / CEO" },
        { key: "co_founder",  label: "Co-Founder",    relationshipType: "governance_role", roleTitle: "Co-Founder" },
        ...employeeRoles,
        { key: "investor",    label: relationshipContext["7"] ?? "Investor", relationshipType: "investor", roleTitle: "Investor" },
        { key: "advisor",     label: "Advisor",                                  relationshipType: "governance_role", roleTitle: "Advisor" },
        { key: "partner",     label: relationshipContext["5"] ?? "Partner",      relationshipType: "partner",  roleTitle: "Partner" },
        { key: "freelancer",  label: subtypes["6"] ?? "Freelancer",              relationshipType: "employee", roleTitle: "Freelancer" },
        { key: "other",       label: "Other",                                    relationshipType: "governance_role", roleTitle: null },
      ];

    case "L1": // Product
      return [
        { key: "founder_ceo", label: "Creator / Maker", relationshipType: "governance_role", roleTitle: "Creator / Maker" },
        { key: "employee",    label: "Product Manager", relationshipType: "employee",        roleTitle: "Product Manager" },
        { key: "investor",    label: relationshipContext["7"] ?? "Investor", relationshipType: "investor", roleTitle: "Investor" },
        { key: "partner",     label: relationshipContext["5"] ?? "Partner",      relationshipType: "partner",  roleTitle: "Partner" },
        { key: "client",      label: relationshipContext["4"] ?? "User / Customer", relationshipType: "client", roleTitle: "User / Customer" },
        { key: "supplier",    label: relationshipContext["3"] ?? "Reseller / Provider", relationshipType: "supplier", roleTitle: "Reseller / Provider" },
        { key: "freelancer",  label: subtypes["6"] ?? "Freelancer",              relationshipType: "employee", roleTitle: "Freelancer" },
        { key: "other",       label: "Other",                                    relationshipType: "governance_role", roleTitle: null },
      ];

    case "L2": // Function / Service
      return [
        { key: "founder_ceo", label: "Owner / Operator", relationshipType: "governance_role", roleTitle: "Owner / Operator" },
        ...employeeRoles,
        { key: "partner",     label: relationshipContext["5"] ?? "Partner", relationshipType: "partner", roleTitle: "Partner" },
        { key: "client",      label: relationshipContext["4"] ?? "User",     relationshipType: "client",  roleTitle: "User" },
        { key: "other",       label: "Other",                               relationshipType: "governance_role", roleTitle: null },
      ];

    case "L3": // Person
      return [
        { key: "employee", label: "Colleague / Team Member", relationshipType: "employee", roleTitle: "Team Member" },
        { key: "partner",  label: relationshipContext["5"] ?? "Partner", relationshipType: "partner", roleTitle: "Partner" },
        { key: "client",   label: relationshipContext["4"] ?? "Client",  relationshipType: "client",  roleTitle: "Client" },
        { key: "other",    label: "Other",                            relationshipType: "governance_role", roleTitle: null },
      ];

    default:
      return [
        ...base,
        ...employeeRoles,
        { key: "other", label: "Other", relationshipType: "governance_role", roleTitle: null },
      ];
  }
}

/**
 * Fixed, level-independent role options for the external-entity wizard path (Step 1 =
 * "External entity"). Deliberately NOT derived from getRoleOptionsForEntityLevel: that
 * per-level list is inconsistent for this purpose — e.g. the L0 (company) list has
 * `investor`/`partner` but no `client`/`supplier` option at all, which would be the
 * single most common real case (adding a company you're a client of). Whichever
 * option gets picked here is what actually persists own-vs-external — via the same
 * `/entity/relationship/add` call the "own" path already uses (see handleBuild in
 * Wizard_Entity_Onboarding.tsx) — there is no separate is_external flag anywhere.
 */
export const EXTERNAL_ROLE_OPTIONS: RoleOption[] = [
  { key: "client",   label: "Client",   relationshipType: "client",   roleTitle: "Client" },
  { key: "supplier", label: "Supplier", relationshipType: "supplier", roleTitle: "Supplier" },
  { key: "partner",  label: "Partner",  relationshipType: "partner",  roleTitle: "Partner" },
  { key: "investor", label: "Investor", relationshipType: "investor", roleTitle: "Investor" },
  // Custom free-text role (StepRole renders the input when "other" is selected). Deliberately
  // "partner" and NOT the own-path default "governance_role": per the note above, the resolved
  // relationship_type is the ONLY thing that persists own-vs-external, and governance_role sits
  // in the backend's OWN_RELATIONSHIP_TYPES — so a custom external role typed here would land
  // classified as one of the user's own entities, silently undoing their "external" choice.
  // "partner" is the neutral member of EXTERNAL_RELATIONSHIP_TYPES; the user's own wording is
  // preserved verbatim in role_title.
  { key: "other",    label: "Other",    relationshipType: "partner",  roleTitle: null },
];

/**
 * Resolve a selected role key to (relationship_type, role_title).
 * Falls back to governance_role with no title.
 *
 * `options`, when passed, is searched instead of the per-level list — required for the
 * external-entity path, since EXTERNAL_ROLE_OPTIONS' keys (client/supplier) aren't
 * present in every level's own list (e.g. L0 has no client/supplier option at all), so
 * looking them up via getRoleOptionsForEntityLevel would silently fall back to
 * "governance_role" — exactly the wrong classification for an external entity.
 */
export function resolveRole(
  roleKey: RoleKey,
  level: string,
  roleOther: string,
  entityIndex?: Record<string, unknown> | null,
  options?: RoleOption[],
): [string, string | null] {
  const option = (options ?? getRoleOptionsForEntityLevel(level, entityIndex)).find((o) => o.key === roleKey);
  if (roleKey === "other") {
    // Take the matched option's relationshipType rather than hardcoding governance_role:
    // that constant is correct for the own path but wrong for EXTERNAL_ROLE_OPTIONS, where
    // it would reclassify the entity as own (see the comment on that list). The custom text
    // the user typed becomes role_title either way.
    return [option?.relationshipType ?? "governance_role", roleOther.trim() || null];
  }
  return [option?.relationshipType ?? "governance_role", option?.roleTitle ?? null];
}
